/**
 * @author Muhammad Omran
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-12-03
 * @description Implementation of my followings page.
 * @filename my-followings.tsx
 */
import { PreviewKind } from "@radivision/graphql";
import React from "react";
import { CarouselItem } from "../../../component-configuration/carousel-item";
import { REACT_USER_CONTEXT } from "../../../contexts/react-user-context";
import { getFollowingItems } from "../../../utilities/general";
import { GridView } from "../../lists/grid-view";
import { FiltersWidget } from "../../user-widgets/filters-widget";

/**
 * interface that defines the state of the component
 *
 * @interface
 */
interface MyFollowingsState {
  /**
   * current selected tab
   *
   * @type {"People" | "Companies"}
   * @memberof MyFollowingsState
   */
  currentTab: "People" | "Companies";

  /**
   * user list of following items
   */
  follows: {
    people?: CarouselItem[];
    entities?: CarouselItem[];
  };
}

/**
 * A React component that renders the MyFollowings component
 *
 * @extends {React.Component}
 */
export class MyFollowings extends React.Component<{}, MyFollowingsState> {
  /**
   * Radivision app user Context
   */
  static contextType = REACT_USER_CONTEXT;

  /**
   * Constructor.
   *
   * @param {MyFollowings} props The props of the component.
   */
  constructor(props: {}) {
    super(props);

    this.state = {
      follows: null,
      currentTab: "People",
    };
  }

  /**
   * Invoked when the component finishs rendering for the very first time
   */
  componentDidMount() {
    if (this.context && this.context.user) {
      const loggedinUser = this.context.user;
      const follows = getFollowingItems(loggedinUser.follows, {
        requestedImageDimensions: {
          containerWidthRatio: 9 / 12,
          numberOfItems: 4,
        },
        isSquareImage: true,
      });

      this.setState({ follows });
    }
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered list.
   */
  render(): React.ReactNode {
    return (
      <div className="my-saved-items">
        <div className="title">
          <h3>Following</h3>
        </div>
        <FiltersWidget
          json={{
            filters: [
              {
                id: "0",
                label: "People",
              },
              {
                id: "1",
                label: "Companies",
              },
            ],
          }}
          alignItems="left"
          resetTopMargin={true}
          hideAll={true}
          disableStickey={true}
          customFilterHandler={(label: string) => {
            const newTab: "People" | "Companies" =
              label === "Companies" ? "Companies" : "People";
            this.setState({ currentTab: newTab });
          }}
        />
        {this.state.currentTab === "People" ? (
          <div className="section-header">
            <div>
              {this.state.follows &&
              this.state.follows.people &&
              this.state.follows.people.length === 0 ? (
                <h4 className="no-content-to-show">No saved people to show</h4>
              ) : this.state.follows !== null &&
                this.state.follows !== undefined &&
                this.state.follows.people ? (
                <GridView
                  gridContent={this.state.follows.people}
                  itemPreviewKind={PreviewKind.CIRCLE}
                  itemsToShow={6}
                  hideItemTypeIcon={true}
                  centerItemTitle={true}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="section-header">
            <div>
              {this.state.follows &&
              this.state.follows.entities &&
              this.state.follows.entities.length === 0 ? (
                <h4 className="no-content-to-show">
                  No saved companies to show
                </h4>
              ) : this.state.follows !== null &&
                this.state.follows !== undefined &&
                this.state.follows.entities ? (
                <GridView
                  gridContent={this.state.follows.entities}
                  pageType="MyFollowings"
                  itemPreviewKind={PreviewKind.CIRCLE}
                  itemsToShow={6}
                  hideItemTypeIcon={true}
                  centerItemTitle={true}
                />
              ) : null}
            </div>
          </div>
        )}
        {/* <div className="section-header">
          <h5>Navigation Resources</h5>

          <div className="container-fluid">
            <GridView gridItemImage={[]} gridItemTitle={[]} isCard />
          </div>
        </div> */}
      </div>
    );
  }
}
